import React, { useEffect, useState } from "react";

import {
    dryRunMarketplaceOrders,
    healthcheckMarketplaceOrders,
    updatePromAccount,
} from "../api/promApi";
import { getBusinessStores } from "../api/suppliersApi";
import { getEnterprises } from "../api/enterpriseApi";
import "../pages/IntegrationsPage.css";

const SOURCE_MODES = [
    { value: "sajt_and_utm", label: "sajt + UTM" },
    { value: "sajt_only", label: "только sajt" },
    { value: "custom_fields", label: "custom fields" },
];


const defaultDraft = {
    orders_enabled: false,
    order_live_send_enabled: false,
    mark_received_after_salesdrive_create: false,
    create_canceled_orders: false,
    source_field_mode: "sajt_and_utm",
    business_store_id: "",
    salesdrive_api_enterprise_code: "",
    order_fetch_interval_seconds: 300,
    order_initial_lookback_days: 7,
    order_overlap_minutes: 10,
    status_sync_enabled: false,
    delivery_ttn_sync_enabled: false,
    prom_cancel_sync_enabled: false,
    prom_delivered_sync_enabled: false,
    prom_paid_sync_enabled: false,
    prom_default_delivery_type: "nova_poshta",
};

const MarketplaceOrdersPanel = ({ account, onAccountUpdated }) => {
    const accountId = account?.id;
    const [draft, setDraft] = useState(defaultDraft);
    const [status, setStatus] = useState("");
    const [busy, setBusy] = useState(false);
    const [dryRun, setDryRun] = useState(null);
    const [stores, setStores] = useState([]);
    const [enterprises, setEnterprises] = useState([]);

    useEffect(() => {
        getBusinessStores().then((rows) => setStores(Array.isArray(rows) ? rows : [])).catch(() => setStores([]));
        getEnterprises().then((rows) => setEnterprises(Array.isArray(rows) ? rows : [])).catch(() => setEnterprises([]));
    }, []);

    useEffect(() => {
        setDraft({
            ...defaultDraft,
            orders_enabled: !!account?.orders_enabled,
            order_live_send_enabled: !!account?.order_live_send_enabled,
            mark_received_after_salesdrive_create: !!account?.mark_received_after_salesdrive_create,
            create_canceled_orders: !!account?.create_canceled_orders,
            source_field_mode: account?.source_field_mode || "sajt_and_utm",
            business_store_id: account?.business_store_id != null ? String(account.business_store_id) : "",
            salesdrive_api_enterprise_code: account?.salesdrive_api_enterprise_code ?? "",
            order_fetch_interval_seconds: account?.order_fetch_interval_seconds ?? 300,
            order_initial_lookback_days: account?.order_initial_lookback_days ?? 7,
            order_overlap_minutes: account?.order_overlap_minutes ?? 10,
            // --- Outbound (статусы/ТТН в Prom) ---
            status_sync_enabled: !!account?.status_sync_enabled,
            delivery_ttn_sync_enabled: !!account?.delivery_ttn_sync_enabled,
            prom_cancel_sync_enabled: !!account?.prom_cancel_sync_enabled,
            prom_delivered_sync_enabled: !!account?.prom_delivered_sync_enabled,
            prom_paid_sync_enabled: !!account?.prom_paid_sync_enabled,
            prom_default_delivery_type: account?.prom_default_delivery_type || "nova_poshta",
        });
        setDryRun(null);
    }, [account]);

    const setField = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

    const save = async () => {
        setBusy(true);
        setStatus("Сохранение настроек заказов...");
        try {
            const payload = {
                ...draft,
                business_store_id: draft.business_store_id === "" ? null : Number(draft.business_store_id),
                salesdrive_api_enterprise_code: draft.salesdrive_api_enterprise_code || null,
                order_fetch_interval_seconds: Number(draft.order_fetch_interval_seconds),
                order_initial_lookback_days: Number(draft.order_initial_lookback_days),
                order_overlap_minutes: Number(draft.order_overlap_minutes),
            };
            const updated = await updatePromAccount(accountId, payload);
            setStatus("Настройки заказов сохранены.");
            if (onAccountUpdated) onAccountUpdated(updated);
        } catch (error) {
            setStatus(`Ошибка сохранения: ${error?.response?.data?.detail || error?.message || error}`);
        } finally {
            setBusy(false);
        }
    };

    const runHealthcheck = async () => {
        setBusy(true);
        setStatus("Проверка токена Prom (Orders read)...");
        try {
            const result = await healthcheckMarketplaceOrders(accountId);
            setStatus(result.ok ? `✅ Healthcheck OK (${result.status_options_count} статусов)` : `❌ ${result.detail}`);
        } catch (error) {
            setStatus(`Ошибка healthcheck: ${error?.message || error}`);
        } finally {
            setBusy(false);
        }
    };

    const runDryRun = async () => {
        setBusy(true);
        setStatus("Dry-run забор заказов (без записи и отправки)...");
        try {
            const result = await dryRunMarketplaceOrders(accountId, 10);
            setDryRun(result);
            setStatus(`Dry-run: fetched=${result.fetched}, mapping_errors=${result.mapping_errors}`);
        } catch (error) {
            setStatus(`Ошибка dry-run: ${error?.message || error}`);
        } finally {
            setBusy(false);
        }
    };

    if (!accountId) {
        return (
            <div className="integrations-page ip-card">
                <div className="ip-card-body">
                    <div className="ip-callout muted">
                        <span className="ip-ic">ℹ</span>
                        <span>Выберите Prom account для настройки заказов.</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="integrations-page ip-card">
            <div className="ip-card-body">
                <div className="ip-block">
                    <div className="ip-bh"><span className="ip-ic">🧭</span><h4>Заказы Prom → SalesDrive</h4></div>
                    <div className="ip-check-row">
                        <label className={`ip-check${draft.orders_enabled ? " ck" : ""}`}>
                            <input className="ip-box" type="checkbox" checked={!!draft.orders_enabled} onChange={(e) => setField("orders_enabled", e.target.checked)} />
                            Polling заказов включён
                        </label>
                        <label className={`ip-check${draft.order_live_send_enabled ? " ck" : ""}`}>
                            <input className="ip-box" type="checkbox" checked={!!draft.order_live_send_enabled} onChange={(e) => setField("order_live_send_enabled", e.target.checked)} />
                            Live send в SalesDrive
                        </label>
                        <label className={`ip-check${draft.mark_received_after_salesdrive_create ? " ck" : ""}`}>
                            <input className="ip-box" type="checkbox" checked={!!draft.mark_received_after_salesdrive_create} onChange={(e) => setField("mark_received_after_salesdrive_create", e.target.checked)} />
                            Ставить Prom status=received
                        </label>
                        <label className={`ip-check${draft.create_canceled_orders ? " ck" : ""}`}>
                            <input className="ip-box" type="checkbox" checked={!!draft.create_canceled_orders} onChange={(e) => setField("create_canceled_orders", e.target.checked)} />
                            Создавать по отменённым
                        </label>
                    </div>
                    <div className="ip-grid4">
                        <label className="ip-fld"><span>Source поля</span>
                            <select className="ip-inp" value={draft.source_field_mode} onChange={(e) => setField("source_field_mode", e.target.value)}>
                                {SOURCE_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                        </label>
                        <label className="ip-fld"><span>BusinessStore (offer scope)</span>
                            <select className="ip-inp" value={String(draft.business_store_id ?? "")} onChange={(e) => setField("business_store_id", e.target.value)}>
                                <option value="">— не выбрано —</option>
                                {stores.map((s) => (
                                    <option key={s.id} value={String(s.id)}>
                                        {s.store_code} — {s.store_name || s.salesdrive_store_name || ""}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="ip-fld"><span>SalesDrive enterprise_code (источник API-ключа)</span>
                            <select className="ip-inp" value={draft.salesdrive_api_enterprise_code ?? ""} onChange={(e) => setField("salesdrive_api_enterprise_code", e.target.value)}>
                                <option value="">— не выбрано —</option>
                                {enterprises.map((ent) => (
                                    <option key={ent.enterprise_code} value={ent.enterprise_code}>
                                        {(ent.enterprise_name || ent.enterprise_code)} ({ent.enterprise_code}){ent.token ? "" : " — без токена"}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="ip-fld"><span>Интервал polling (сек)</span>
                            <input className="ip-inp" type="number" value={draft.order_fetch_interval_seconds} onChange={(e) => setField("order_fetch_interval_seconds", e.target.value)} />
                        </label>
                        <label className="ip-fld"><span>Lookback (дней, bootstrap)</span>
                            <input className="ip-inp" type="number" value={draft.order_initial_lookback_days} onChange={(e) => setField("order_initial_lookback_days", e.target.value)} />
                        </label>
                        <label className="ip-fld"><span>Overlap (мин)</span>
                            <input className="ip-inp" type="number" value={draft.order_overlap_minutes} onChange={(e) => setField("order_overlap_minutes", e.target.value)} />
                        </label>
                    </div>
                </div>

                <div className="ip-block">
                    <div className="ip-bh"><span className="ip-ic">🔁</span><h4>Outbound в Prom (статусы / ТТН)</h4></div>
                    <p className="ip-lead" style={{ margin: "-6px 0 0" }}>
                        Активная отправка (received + ТТН) идёт из business-процессора; отказ — из SalesDrive webhook.
                        Боевая отправка гейтится «Live send в SalesDrive» + флагами ниже.
                    </p>
                    <div className="ip-check-row">
                        <label className={`ip-check${draft.status_sync_enabled ? " ck" : ""}`}>
                            <input className="ip-box" type="checkbox" checked={!!draft.status_sync_enabled} onChange={(e) => setField("status_sync_enabled", e.target.checked)} />
                            Синк статусов (received/custom)
                        </label>
                        <label className={`ip-check${draft.delivery_ttn_sync_enabled ? " ck" : ""}`}>
                            <input className="ip-box" type="checkbox" checked={!!draft.delivery_ttn_sync_enabled} onChange={(e) => setField("delivery_ttn_sync_enabled", e.target.checked)} />
                            Синк ТТН (save_declaration_id)
                        </label>
                        <label className={`ip-check${draft.prom_cancel_sync_enabled ? " ck" : ""}`}>
                            <input className="ip-box" type="checkbox" checked={!!draft.prom_cancel_sync_enabled} onChange={(e) => setField("prom_cancel_sync_enabled", e.target.checked)} />
                            Синк SalesDrive «Отказ» (6) → Prom canceled
                        </label>
                        <label className={`ip-check${draft.prom_delivered_sync_enabled ? " ck" : ""}`}>
                            <input className="ip-box" type="checkbox" checked={!!draft.prom_delivered_sync_enabled} onChange={(e) => setField("prom_delivered_sync_enabled", e.target.checked)} />
                            Синк delivered
                        </label>
                        <label className={`ip-check${draft.prom_paid_sync_enabled ? " ck" : ""}`}>
                            <input className="ip-box" type="checkbox" checked={!!draft.prom_paid_sync_enabled} onChange={(e) => setField("prom_paid_sync_enabled", e.target.checked)} />
                            Синк paid
                        </label>
                    </div>
                    <div className="ip-grid4">
                        <label className="ip-fld" style={{ maxWidth: "320px" }}><span>Перевозчик по умолчанию</span>
                            <select className="ip-inp" value={draft.prom_default_delivery_type} onChange={(e) => setField("prom_default_delivery_type", e.target.value)}>
                                <option value="nova_poshta">nova_poshta</option>
                                <option value="ukrposhta">ukrposhta</option>
                                <option value="meest">meest</option>
                            </select>
                        </label>
                    </div>
                </div>

                {account?.last_order_error && (
                    <div className="ip-callout danger">
                        <span className="ip-ic">✕</span>
                        <span>Последняя ошибка: {account.last_order_error}</span>
                    </div>
                )}

                <div className="ip-save-bar">
                    {status && <span className="ip-note">{status}</span>}
                    <button type="button" className="ip-btn" disabled={busy} onClick={runHealthcheck}>Healthcheck Prom</button>
                    <button type="button" className="ip-btn" disabled={busy} onClick={runDryRun}>Dry-run fetch</button>
                    <button type="button" className="ip-btn ip-btn-primary" disabled={busy} onClick={save}>Сохранить настройки</button>
                </div>

                {dryRun && (
                    <pre style={{ margin: 0, padding: "12px", borderRadius: "8px", backgroundColor: "var(--ip-surface-2)", overflow: "auto", maxHeight: "240px", fontSize: "12px" }}>
                        {JSON.stringify(dryRun.orders, null, 2)}
                    </pre>
                )}
            </div>
        </div>
    );
};

export default MarketplaceOrdersPanel;
